import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useFilteredArtists, useProgram } from './utils/SpilleplanHooks';
import SpilleplanDay from './SpilleplanDay';
import gsap from 'gsap';
import { Draggable, ScrollTrigger, InertiaPlugin, ScrollToPlugin } from 'gsap/all';
import DateButtonList from './DateButtonList';
import PageHandler from '@client/core/PageSwitch/PageHandler';

const Container = styled.div`
	width: 100%;
	position: relative;
	padding-top: 100px;
	overflow-x: auto;
	::-webkit-scrollbar {
		display: none;
	}
	-ms-overflow-style: none; /* IE and Edge */
	scrollbar-width: none; /* Firefox */
`;

const Inner = styled.div`
	display: flex;
	flex-direction: column;
	width: fit-content;
`;

const Program = styled.div<{ $days: number }>`
	display: flex;

	@supports (grid-template-rows: subgrid) {
		display: grid;
		grid-template-columns: repeat(${props => props.$days}, max-content);
		// amount of locations + timeline + footer
		grid-template-rows: repeat(6, auto);
		grid-auto-flow: column;
	}
`;

interface IProps {
	artists: any[];
	onDayChange: (index: number) => void;
	filter?: [{ name: string; id: string }];
}

export default function Spilleplan(props: IProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const draggable = useRef<Draggable>(null);

	const artists = useFilteredArtists(props.artists, props.filter);
	const program = useProgram(artists);

	const [activeIndex, setActiveIndex] = useState(0);

	// register draggable
	useEffect(() => {
		gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, Draggable, InertiaPlugin);

		draggable.current = Draggable.create(containerRef.current, {
			type: 'scrollLeft',
			throwProps: true,
			throwResistance: 1,
			inertia: true,
			maxDuration: 0.5,
			edgeResistance: 0.65,
			allowContextMenu: true,
			allowNativeTouchScrolling: true,
			lockAxis: false,
			onDragEnd: () => {
				if (!draggable.current) return;
				const position = draggable.current?.endX * -1;
				if (!isNaN(position)) {
					PageHandler.getInstance().setData({ progress: position });
				}
			},
		})[0];

		const state = PageHandler.getInstance().getData<{ progress: number }>();
		if (state?.progress) {
			gsap.to(containerRef.current, {
				duration: 0,
				scrollTo: { x: state?.progress, autoKill: true },
			});
		}

		return () => {
			if (draggable.current) draggable.current?.kill();
		};
	}, [containerRef.current]);

	// call onDayChange
	useEffect(() => {
		props.onDayChange(activeIndex);
	}, [activeIndex]);

	// move scroll position to index
	const moveTo = useCallback(
		(index: number) => {
			if (!containerRef.current) return;
			const targets = Array.from(containerRef.current.querySelectorAll<HTMLElement>('.ProgramDay'));
			const target = targets[index];

			if (target) {
				PageHandler.getInstance().setData({ progress: target.offsetLeft });
				gsap.to(containerRef.current, {
					duration: 0.5,
					scrollTo: { x: target.offsetLeft, autoKill: true },
					ease: 'power2.out',
				});
			}
		},
		[containerRef.current]
	);

	return (
		<Container ref={containerRef} id='spilleplan'>
			<Inner>
				<DateButtonList events={artists} active={activeIndex} setActive={moveTo} />
				<Program $days={program.length}>
					{program.map((day, i) => (
						<SpilleplanDay
							key={'spilleplanEntry_' + i + '_' + day.date.toString()}
							events={day.events}
							pointerIllustration={i < 3}
							onEnterViewport={() => setActiveIndex(i)}
						/>
					))}
				</Program>
			</Inner>
		</Container>
	);
}
